/* Beautisupply storefront behaviour for the bs- sections: reveal on scroll, rails, the pinned bottle story,
   sticky bars, the gallery, the film and the restock form. No dependencies; three.js is loaded on demand. */
(() => {
  if (window.BS) return;
  const BS = (window.BS = {});
  const doc = document;
  const root = doc.documentElement;
  const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const still = /[?&]bs-still\b/.test(location.search); // review switch: freeze everything in its final state
  const clamp = (v, a = 0, b = 1) => Math.min(b, Math.max(a, v));
  const onReady = (fn) => (doc.readyState === 'loading' ? doc.addEventListener('DOMContentLoaded', fn) : fn());
  // Heavy extras (the 3D bottle) wait until the page has loaded and the browser has a quiet moment, so the first
  // screen and the scroll never wait for them; the still poster shows until then (11 October 2026).
  const calm = new Promise((done) => {
    const idle = () => ('requestIdleCallback' in window ? requestIdleCallback(() => done(), { timeout: 1500 }) : setTimeout(done, 300));
    if (doc.readyState === 'complete') idle();
    else addEventListener('load', idle, { once: true });
  });
  if (reduce || still) root.classList.add('bs-noanim');

  /* ---- reveal ---- */
  function initReveal(scope) {
    const els = scope.querySelectorAll('[data-bs-reveal]:not(.is-in)');
    if (!els.length) return;
    if (reduce || still || !('IntersectionObserver' in window)) {
      els.forEach((el) => el.classList.add('is-in'));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (!e.isIntersecting) return;
          e.target.classList.add('is-in');
          io.unobserve(e.target);
        });
      },
      { rootMargin: '0px 0px -12% 0px', threshold: 0.08 }
    );
    els.forEach((el) => io.observe(el));
  }

  /* ---- horizontal rails ---- */
  function initRails(scope) {
    scope.querySelectorAll('[data-bs-rail]').forEach((rail) => {
      if (rail.bsReady) return;
      rail.bsReady = true;
      const track = rail.querySelector('.bs-rail__track');
      const prev = rail.querySelector('[data-bs-prev]');
      const next = rail.querySelector('[data-bs-next]');
      if (!track || !prev || !next) return;
      const step = () => {
        const first = track.firstElementChild;
        if (!first) return track.clientWidth * 0.8;
        const gap = parseFloat(getComputedStyle(track).columnGap || '20') || 20;
        return first.getBoundingClientRect().width + gap;
      };
      const update = () => {
        const max = track.scrollWidth - track.clientWidth - 2;
        prev.disabled = track.scrollLeft <= 2;
        next.disabled = track.scrollLeft >= max;
      };
      prev.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: reduce ? 'auto' : 'smooth' }));
      next.addEventListener('click', () => track.scrollBy({ left: step(), behavior: reduce ? 'auto' : 'smooth' }));
      track.addEventListener('scroll', update, { passive: true });
      addEventListener('resize', update);
      update();
    });
  }

  /* ---- pinned bottle story ---- */
  function webglOK() {
    if (typeof window.BS_GL === 'boolean') return window.BS_GL; // already asked by the story section as it was drawn
    try {
      const c = doc.createElement('canvas');
      return !!(window.WebGLRenderingContext && (c.getContext('webgl2') || c.getContext('webgl')));
    } catch (e) {
      return false;
    }
  }

  function initStory(el) {
    if (el.bsReady) return;
    el.bsReady = true;
    const sticky = el.querySelector('.bs-story__sticky');
    const canvas = el.querySelector('.bs-story__canvas');
    const glow = el.querySelector('.bs-story__glow');
    const steps = [...el.querySelectorAll('.bs-story__step')].map((node) => ({ node, from: parseFloat(node.dataset.from), to: parseFloat(node.dataset.to) }));
    const dots = [...el.querySelectorAll('.bs-story__dots i')];
    const saveData = navigator.connection && navigator.connection.saveData;
    if (reduce || saveData || !webglOK() || !canvas) {
      el.classList.add('is-static');
      return;
    }
    const hero = el.dataset.mode === 'hero';
    if (!hero) el.style.height = `${Math.max(steps.length, 1) * 100 + 40}vh`;
    // first thing on the page: pull it up under the header so the pinned stage always fills the screen. The section's
    // own script has normally done it already, before the first paint (data-bs-gap); this is the fallback.
    let headGap = 0;
    if (el.hasAttribute('data-bs-gap')) {
      headGap = parseInt(el.getAttribute('data-bs-gap'), 10) || 0;
    } else {
      const top = el.getBoundingClientRect().top + scrollY;
      if (top < innerHeight * 0.4) {
        headGap = Math.round(top);
        el.style.marginTop = `${-headGap}px`;
        root.classList.add('bs-hero-under');
      }
    }
    // while the header still covers the top of the stage, fit the picture and the text into the part that shows
    let lastGap = -1;
    const setGap = () => {
      const g = Math.max(0, headGap - scrollY);
      if (g !== lastGap) {
        lastGap = g; // written only when it changes: a write per scroll frame restyled the whole stage on phones
        sticky.style.setProperty('--bs-gap', `${g}px`);
        sticky.style.setProperty('--bs-gap-r', (g / Math.max(1, innerHeight)).toFixed(4));
      }
      if (headGap) root.classList.toggle('bs-at-top', scrollY < Math.max(8, headGap * 0.5));
    };
    setGap();
    let stage = null;
    let loading = false;
    let inView = false;
    let progress = 0;
    const mq = matchMedia('(max-width: 899px)');
    const layout = () => (mq.matches ? { layout: 'stack' } : { layout: 'split' });

    const measure = () => {
      if (hero) return (progress = 0);
      const r = el.getBoundingClientRect();
      const span = el.offsetHeight - innerHeight;
      progress = span > 0 ? clamp(-r.top / span) : 0;
      return progress;
    };
    const paint = () => {
      const p = measure();
      let active = 0;
      steps.forEach((s, i) => {
        const on = p >= s.from && p <= s.to;
        s.node.classList.toggle('is-on', on);
        if (p >= s.from) active = i;
      });
      dots.forEach((d, i) => d.classList.toggle('is-on', i === active));
      el.classList.toggle('is-moving', p > 0.02);
      if (stage) stage.set(p);
    };
    const load = async () => {
      if (stage || loading) return;
      loading = true;
      try {
        await calm;
        const THREE = await import(el.dataset.three);
        const mod = await import(el.dataset.bottle);
        stage = mod.createBottleStage(canvas, THREE, { label: el.dataset.label, skin: el.dataset.skin || 'max', mobile: mq.matches, ...layout() });
        stage.onFrame = (f) => {
          if (glow) {
            glow.style.transform = `translate3d(${f.dropCenter.x}px, ${f.dropCenter.y}px, 0)`;
            glow.style.opacity = String(f.uv * (0.5 + 0.5 * (1 - f.cured)));
          }
          sticky.style.setProperty('--bs-uv', f.uv.toFixed(3));
        };
        await stage.loaded;
        stage.set(measure(), true);
        el.classList.add('is-live');
        if (inView && !still) stage.start();
        BS.story = { el, stage, set: (t) => stage.set(t, true) };
        root.dispatchEvent(new CustomEvent('bs:story-ready'));
      } catch (err) {
        el.classList.add('is-static');
        el.style.height = '';
        console.warn('Bottle stage unavailable', err);
      }
    };

    let ticking = false;
    let hold = 0;
    const onScroll = () => {
      // phones, the hero (not the pinned story): the bottle's idle motion pauses while the finger scrolls, so the
      // page has the processor and the graphics to itself, and picks up again a moment after the scroll stops
      if (hero && stage && mq.matches && inView && !still) {
        stage.stop();
        clearTimeout(hold);
        hold = setTimeout(() => inView && stage.start(), 180);
      }
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        ticking = false;
        setGap();
        if (inView) paint();
      });
    };
    addEventListener('scroll', onScroll, { passive: true });
    addEventListener('resize', () => {
      if (stage) {
        stage.setLayout(layout().layout);
        stage.resize();
      }
      paint();
    });
    new IntersectionObserver(
      (entries) => {
        inView = entries[0].isIntersecting;
        if (inView) {
          load();
          paint();
          if (stage && !still) stage.start();
        } else if (stage) stage.stop();
      },
      { rootMargin: '60% 0px 60% 0px' }
    ).observe(el);
    paint();
  }

  /* ---- product bars ---- */
  function initBars() {
    const bar = doc.querySelector('[data-bs-bar]');
    const buybar = doc.querySelector('[data-bs-buybar]');
    const header = doc.querySelector('.shopify-section-header, .section-header');
    if (bar) {
      const after = (bar.dataset.after && doc.querySelector(bar.dataset.after)) || null;
      const darks = [...doc.querySelectorAll('.bs-story:not(.bs-story--pro), .bs-section--dark')];
      const update = () => {
        const limit = after ? after.getBoundingClientRect().top + scrollY + Math.min(after.offsetHeight, innerHeight) * 0.6 : innerHeight * 0.7;
        bar.classList.toggle('is-on', scrollY > limit);
        const probe = bar.getBoundingClientRect().bottom + 1;
        let dark = false;
        for (const d of darks) {
          const r = d.getBoundingClientRect();
          if (r.top <= probe && r.bottom >= probe) dark = true;
        }
        bar.classList.toggle('is-dark', dark);
        let shift = 0;
        if (header) {
          const r = header.getBoundingClientRect();
          if (r.bottom > 0 && getComputedStyle(header).position !== 'static') shift = Math.max(0, r.bottom);
        }
        bar.style.setProperty('--bs-bar-shift', `${shift}px`);
      };
      // once per frame at most: it measures several sections, and phones fire many scroll events per frame
      let barTick = false;
      const onBarScroll = () => {
        if (barTick) return;
        barTick = true;
        requestAnimationFrame(() => {
          barTick = false;
          update();
        });
      };
      addEventListener('scroll', onBarScroll, { passive: true });
      addEventListener('resize', update);
      if (header && 'MutationObserver' in window) new MutationObserver(() => setTimeout(update, 320)).observe(header, { attributes: true, attributeFilter: ['class'] });
      update();
    }
    if (buybar || doc.querySelector('.bs-pdp-top--soldout')) root.classList.add('bs-no-appbar');
    if (buybar && 'IntersectionObserver' in window) {
      const watch = [...doc.querySelectorAll('[data-bs-buy-main]')];
      const seen = new Map();
      const refresh = () => {
        const anyVisible = [...seen.values()].some(Boolean);
        const on = !anyVisible && scrollY > 200;
        buybar.classList.toggle('is-on', on);
        root.classList.toggle('bs-buybar-on', on);
      };
      const io = new IntersectionObserver((entries) => {
        entries.forEach((e) => seen.set(e.target, e.isIntersecting));
        refresh();
      });
      watch.forEach((w) => io.observe(w));
      addEventListener('scroll', refresh, { passive: true });
    }
  }

  /* ---- buy boxes: keep the price on the button and the sticky bar in step with the chosen quantity ---- */
  function initBuy(scope) {
    scope.querySelectorAll('[data-bs-buy]').forEach((form) => {
      if (form.bsReady) return;
      form.bsReady = true;
      const out = form.querySelectorAll('[data-bs-total]');
      const sync = () => {
        const picked = form.querySelector('input[name="quantity"]:checked');
        if (!picked) return;
        out.forEach((o) => (o.textContent = picked.dataset.total || ''));
        doc.querySelectorAll('[data-bs-total-mirror]').forEach((o) => (o.textContent = picked.dataset.total || ''));
        doc.querySelectorAll('[data-bs-qty-mirror]').forEach((o) => (o.value = picked.value));
      };
      form.addEventListener('change', sync);
      sync();
    });
  }

  /* ---- products with options: keep the variant id, price and sold-out state in step ---- */
  function money(cents, format) {
    const amount = (cents / 100).toFixed(2);
    const short = amount.endsWith('.00') ? amount.slice(0, -3) : amount;
    const withCommas = short.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return (format || '${{amount}}').replace(/\{\{\s*amount[a-z_]*\s*\}\}/, withCommas);
  }

  function initVariants(scope) {
    scope.querySelectorAll('[data-bs-variants]').forEach((box) => {
      if (box.bsReady) return;
      box.bsReady = true;
      const form = box.querySelector('form');
      const qty = form.querySelector('input[name="quantity"]');
      form.querySelectorAll('[data-bs-step]').forEach((b) =>
        b.addEventListener('click', () => {
          qty.value = String(Math.max(1, (parseInt(qty.value, 10) || 1) + parseInt(b.dataset.bsStep, 10)));
          qty.dispatchEvent(new Event('change', { bubbles: true }));
        })
      );
      const json = box.querySelector('[data-bs-variants-json]');
      const idInput = form.querySelector('[data-bs-variant-id]');
      const submit = form.querySelector('[data-bs-variant-submit]');
      const label = form.querySelector('[data-bs-variant-label]');
      const price = form.querySelector('[data-bs-variant-price]');
      const soldout = box.querySelector('[data-bs-variant-soldout]');
      let variants = [];
      try {
        variants = json ? JSON.parse(json.textContent) : [];
      } catch (e) {
        variants = [];
      }
      const base = price ? Number(price.dataset.cents || 0) : 0;
      let current = variants.find((v) => idInput && String(v.id) === idInput.value) || null;
      const sync = () => {
        if (variants.length) {
          const chosen = [...form.querySelectorAll('input[data-option]:checked')].sort((a, b) => a.dataset.option - b.dataset.option).map((i) => i.value);
          current = variants.find((v) => v.options.every((o, i) => o === chosen[i])) || null;
          if (current && idInput) idInput.value = current.id;
          const ok = !!(current && current.available);
          submit.disabled = !ok;
          label.textContent = ok ? 'Add to cart' : current ? 'Sold out' : 'Unavailable';
          if (soldout) soldout.hidden = ok;
          const note = box.querySelector('input[name="contact[Product]"]');
          if (note && current) note.value = `${note.dataset.base || (note.dataset.base = note.value)} (${current.title})`;
        }
        const unit = current ? current.price : base;
        const count = Math.max(1, parseInt(qty.value, 10) || 1);
        if (unit && price) price.textContent = money(unit * count, box.dataset.money);
        // the buy bar at the bottom of the screen adds what is chosen here: option, quantity, price, sold-out state
        const bar = doc.querySelector('[data-bs-buybar]');
        if (bar) {
          const buyable = !variants.length || !!(current && current.available);
          const barId = bar.querySelector('input[name="id"]');
          const barQty = bar.querySelector('[data-bs-qty-mirror]');
          const barBtn = bar.querySelector('button[type="submit"]');
          const barLabel = bar.querySelector('[data-bs-bar-label]');
          const barTotal = bar.querySelector('[data-bs-total-mirror]');
          if (current && barId) barId.value = current.id;
          if (barQty) barQty.value = String(count);
          if (barBtn) barBtn.disabled = !buyable;
          if (barLabel) barLabel.textContent = buyable ? 'Add to cart' : current ? 'Sold out' : 'Unavailable';
          if (barTotal && unit) barTotal.textContent = money(unit * count, box.dataset.money);
        }
        const topPrice = doc.querySelector('[data-bs-bar] .bs-bar__price');
        if (topPrice && unit) topPrice.textContent = money(unit, box.dataset.money);
      };
      form.addEventListener('change', sync);
      if (variants.length) sync();
    });
  }

  /* ---- gallery ---- */
  function initGallery(scope) {
    scope.querySelectorAll('[data-bs-gallery]').forEach((gal) => {
      if (gal.bsReady) return;
      gal.bsReady = true;
      const track = gal.querySelector('.bs-gal__track');
      const thumbs = [...gal.querySelectorAll('.bs-gal__thumb')];
      const slides = [...gal.querySelectorAll('.bs-gal__slide')];
      if (!track) return;
      const setActive = (i) => thumbs.forEach((t, k) => t.classList.toggle('is-on', k === i));
      thumbs.forEach((t, i) =>
        t.addEventListener('click', () => {
          track.scrollTo({ left: slides[i].offsetLeft, behavior: reduce ? 'auto' : 'smooth' });
          setActive(i);
        })
      );
      let raf = 0;
      track.addEventListener(
        'scroll',
        () => {
          cancelAnimationFrame(raf);
          raf = requestAnimationFrame(() => {
            const i = Math.round(track.scrollLeft / Math.max(1, track.clientWidth));
            setActive(i);
            slides.forEach((s, k) => {
              const v = s.querySelector('video');
              if (v && k !== i && !v.paused) v.pause();
            });
          });
        },
        { passive: true }
      );
    });
  }

  /* ---- film ---- */
  function initFilm(scope) {
    scope.querySelectorAll('[data-bs-film]').forEach((film) => {
      if (film.bsReady) return;
      film.bsReady = true;
      const video = film.querySelector('video');
      const btn = film.querySelector('.bs-film__play');
      if (!video || !btn) return;
      const toggle = () => {
        if (video.paused) {
          video.muted = false;
          video.play().catch(() => {
            video.muted = true;
            video.play().catch(() => {});
          });
        } else video.pause();
      };
      btn.addEventListener('click', toggle);
      video.addEventListener('play', () => film.classList.add('is-playing'));
      video.addEventListener('pause', () => film.classList.remove('is-playing'));
      video.addEventListener('ended', () => film.classList.remove('is-playing'));
    });
  }

  /* ---- videos in use: one plays at a time, with sound, on a tap ---- */
  function initClips(scope) {
    scope.querySelectorAll('[data-bs-clip]').forEach((frame) => {
      if (frame.bsReady) return;
      frame.bsReady = true;
      const video = frame.querySelector('video');
      if (!video) return;
      frame.addEventListener('click', () => {
        if (!video.paused) return video.pause();
        doc.querySelectorAll('[data-bs-clip] video').forEach((v) => v !== video && v.pause());
        video.muted = false;
        video.play().catch(() => {
          video.muted = true;
          video.play().catch(() => {});
        });
      });
      video.addEventListener('play', () => frame.classList.add('is-playing'));
      video.addEventListener('pause', () => frame.classList.remove('is-playing'));
    });
  }

  /* ---- review wall: cards dealt into columns by the section's own script (window.bsSplitWall), dealt again when
     the window crosses a breakpoint; the first cards show, a button shows the rest (without scripts every card
     shows) ---- */
  function splitWalls() {
    if (window.bsSplitWall) doc.querySelectorAll('[data-bs-wall]').forEach((w) => window.bsSplitWall(w));
  }
  function initWalls(scope) {
    splitWalls();
    if (!BS.wallResize) {
      BS.wallResize = true;
      addEventListener('resize', splitWalls, { passive: true });
    }
    scope.querySelectorAll('[data-bs-wall-more]').forEach((btn) => {
      if (btn.bsReady) return;
      btn.bsReady = true;
      const wall = btn.closest('.bs-wall').querySelector('[data-bs-wall]');
      btn.addEventListener('click', () => {
        wall.classList.remove('is-collapsed');
        btn.setAttribute('aria-expanded', 'true');
        btn.closest('.bs-wall__more').hidden = true;
        const next = wall.querySelector('[data-bs-extra]');
        if (!next) return;
        next.setAttribute('tabindex', '-1');
        next.focus({ preventScroll: true });
      });
    });
  }

  /* ---- shipping map: the day buttons light the map and the list up to that day ---- */
  function initShipmap(scope) {
    scope.querySelectorAll('[data-bs-shipmap]').forEach((box) => {
      if (box.bsReady) return;
      box.bsReady = true;
      const rows = [...box.querySelectorAll('[data-bs-ship-row]')];
      const pills = [...box.querySelectorAll('[data-bs-day]')];
      const paths = [...box.querySelectorAll('.bs-map__r')];
      const stage = box.querySelector('.bs-shipmap__stage');
      const last = parseInt(box.dataset.last, 10) || 5;
      const dayOf = {};
      rows.forEach((row) => row.dataset.codes.split(',').forEach((c) => (dayOf[c] = parseInt(row.dataset.day, 10))));
      paths.forEach((p) => (p.bsDay = dayOf[p.dataset.r] || last)); // places without their own row light up last
      const set = (day, flash) => {
        paths.forEach((p) => {
          const on = p.bsDay <= day;
          if (on && flash && !p.classList.contains('is-lit')) {
            p.classList.remove('is-new');
            void p.getBoundingClientRect();
            p.classList.add('is-new');
          }
          p.classList.toggle('is-lit', on);
        });
        rows.forEach((row) => row.classList.toggle('is-lit', parseInt(row.dataset.day, 10) <= day));
        pills.forEach((b) => {
          const on = parseInt(b.dataset.bsDay, 10) === day;
          b.classList.toggle('is-on', on);
          b.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
      };
      box.classList.add('is-armed');
      set(last, false);
      let timer = 0;
      const stop = () => {
        clearInterval(timer);
        timer = 0;
      };
      pills.forEach((b) =>
        b.addEventListener('click', () => {
          stop();
          set(parseInt(b.dataset.bsDay, 10), true);
        })
      );
      const link = (row, codes, on) => {
        row.classList.toggle('is-hot', on);
        paths.forEach((p) => codes.includes(p.dataset.r) && p.classList.toggle('is-hot', on));
      };
      rows.forEach((row) => {
        const codes = row.dataset.codes.split(',');
        row.addEventListener('mouseenter', () => link(row, codes, true));
        row.addEventListener('mouseleave', () => link(row, codes, false));
        paths.forEach((p) => {
          if (!codes.includes(p.dataset.r)) return;
          p.addEventListener('mouseenter', () => link(row, codes, true));
          p.addEventListener('mouseleave', () => link(row, codes, false));
        });
      });
      if (reduce || still || !stage || !('IntersectionObserver' in window)) return;
      // the first time the map is on screen, play the days through once
      const io = new IntersectionObserver(
        (entries) => {
          if (!entries[0].isIntersecting) return;
          io.disconnect();
          let day = 0;
          set(0, false);
          timer = setInterval(() => {
            day += 1;
            set(day, true);
            if (day >= last) stop();
          }, 850);
        },
        { threshold: 0.8 }
      );
      io.observe(stage);
    });
  }

  /* ---- science: single molecules (short bars) drift as a liquid; where the light has passed they line up end
     to end into chains. The light front follows the scroll position, so it also runs backwards. ---- */
  function initChain(scope) {
    scope.querySelectorAll('[data-bs-chain]').forEach((box) => {
      if (box.bsReady) return;
      box.bsReady = true;
      const stage = box.querySelector('.bs-chain__stage');
      const canvas = box.querySelector('canvas');
      const ctx = canvas && canvas.getContext ? canvas.getContext('2d') : null;
      if (!ctx || !stage) return;
      let W = 0;
      let H = 0;
      let items = [];
      let geo = null;
      let visible = false;
      let raf = 0;
      const build = () => {
        const r = stage.getBoundingClientRect();
        const dpr = Math.min(2, window.devicePixelRatio || 1);
        W = r.width;
        H = r.height;
        if (!W || !H) return;
        canvas.width = Math.round(W * dpr);
        canvas.height = Math.round(H * dpr);
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        const small = W < 640;
        const len = small ? 15 : 21;
        const gap = small ? 4 : 5;
        const rows = Math.max(5, Math.round(H / (small ? 30 : 40)));
        const per = Math.ceil((W + len * 2) / (len + gap)) + 1;
        let seed = 11; // fixed seed: the same picture on every visit
        const rnd = () => (seed = (seed * 16807) % 2147483647) / 2147483647;
        items = [];
        for (let row = 0; row < rows; row++) {
          const y0 = ((row + 0.5) * H) / rows;
          const off = (row % 2) * (len + gap) * 0.5 - len;
          for (let i = 0; i < per; i++) {
            const tx = off + i * (len + gap);
            items.push({ tx, y0, ph: row * 0.9, fx: tx + (rnd() - 0.5) * 90, fy: H * (0.06 + rnd() * 0.88), fa: rnd() * Math.PI, w: 0.25 + rnd() * 0.5, d: rnd() * 6.283, k: 0 });
          }
        }
        geo = { len, thick: small ? 4.5 : 6, amp: small ? 5 : 8, freq: (Math.PI * 2) / (small ? 260 : 420) };
      };
      const progress = () => {
        if (reduce || still) return 1;
        const r = stage.getBoundingClientRect();
        const start = innerHeight * 0.92;
        const end = innerHeight * 0.3;
        return clamp((start - r.top) / (start - end + r.height * 0.35));
      };
      const draw = (time, settle) => {
        if (!geo) return;
        const t = time / 1000;
        const p = progress();
        const front = p * (W + 260) - 130; // the light front, in px from the left
        ctx.clearRect(0, 0, W, H);
        if (p > 0.004 && p < 0.996) {
          const fade = Math.min(1, p / 0.06, (1 - p) / 0.06);
          // a soft glow that trails behind the front and fades ahead of it, with no hard edge
          const g = ctx.createLinearGradient(front - 340, 0, front + 170, 0);
          g.addColorStop(0, 'rgba(124, 77, 255, 0)');
          g.addColorStop(0.45, `rgba(124, 77, 255, ${0.14 * fade})`);
          g.addColorStop(0.66, `rgba(160, 125, 255, ${0.3 * fade})`);
          g.addColorStop(0.8, `rgba(150, 112, 255, ${0.2 * fade})`);
          g.addColorStop(1, 'rgba(124, 77, 255, 0)');
          ctx.fillStyle = g;
          ctx.fillRect(front - 340, 0, 510, H);
        }
        ctx.lineCap = 'round';
        ctx.lineWidth = geo.thick;
        for (const it of items) {
          it.k += ((it.tx < front ? 1 : 0) - it.k) * (settle ? 1 : 0.14);
          const k = it.k * it.k * (3 - 2 * it.k);
          const ax = it.fx + Math.sin(t * it.w + it.d) * 9; // loose: drifting
          const ay = it.fy + Math.cos(t * it.w * 0.8 + it.d) * 9;
          const aa = it.fa + Math.sin(t * it.w * 0.6 + it.d) * 0.9;
          const wave = it.tx * geo.freq + it.ph; // linked: a place on a gently waving chain
          const by = it.y0 + Math.sin(wave) * geo.amp;
          const ba = Math.atan(Math.cos(wave) * geo.amp * geo.freq);
          let turn = (ba - aa) % Math.PI;
          if (turn > Math.PI / 2) turn -= Math.PI;
          if (turn < -Math.PI / 2) turn += Math.PI;
          const x = ax + (it.tx - ax) * k;
          const y = ay + (by - ay) * k;
          const a = aa + turn * k;
          const hx = Math.cos(a) * geo.len * 0.5;
          const hy = Math.sin(a) * geo.len * 0.5;
          const near = p > 0.004 && p < 0.996 ? clamp(1 - Math.abs(it.tx - front) / 150) : 0;
          const alpha = Math.min(1, 0.26 + 0.66 * k + near * 0.3);
          ctx.strokeStyle = `rgba(${Math.round(255 - 70 * near)}, ${Math.round(255 - 92 * near)}, 255, ${alpha.toFixed(3)})`;
          ctx.beginPath();
          ctx.moveTo(x - hx, y - hy);
          ctx.lineTo(x + hx, y + hy);
          ctx.stroke();
        }
      };
      const loop = (time) => {
        draw(time, false);
        raf = visible ? requestAnimationFrame(loop) : 0;
      };
      build();
      BS.chain = { draw: () => draw(performance.now(), true), progress };
      if (reduce || still) {
        draw(0, true);
        addEventListener('resize', () => {
          build();
          draw(0, true);
        });
        return;
      }
      let resizeTimer = 0;
      addEventListener('resize', () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(build, 150);
      });
      if (!('IntersectionObserver' in window)) return draw(0, true);
      new IntersectionObserver(
        (entries) => {
          visible = entries[0].isIntersecting;
          if (visible && !raf) raf = requestAnimationFrame(loop);
        },
        { rootMargin: '10% 0px 10% 0px' }
      ).observe(stage);
    });
  }

  /* ---- restock form: send without leaving the page; fall back to a normal submit if anything is in the way ---- */
  function initNotify(scope) {
    scope.querySelectorAll('[data-bs-notify]').forEach((box) => {
      if (box.bsReady) return;
      box.bsReady = true;
      const form = box.querySelector('form');
      const ok = box.querySelector('[data-bs-notify-ok]');
      if (!form || !ok || !window.fetch) return;
      form.addEventListener('submit', async (ev) => {
        if (form.dataset.native === '1') return;
        ev.preventDefault();
        const btn = form.querySelector('button[type="submit"]');
        if (btn) btn.setAttribute('aria-disabled', 'true');
        try {
          const res = await fetch(form.action, { method: 'POST', body: new FormData(form), headers: { Accept: 'text/html' } });
          if (!res.ok || /\/challenge/.test(res.url)) throw new Error('needs a normal submit');
          const second = box.querySelector('form[data-bs-notify-list]');
          const news = form.querySelector('[data-bs-notify-news]');
          if (second && news && news.checked) {
            const fd = new FormData(second);
            fd.set('contact[email]', form.querySelector('input[type="email"]').value);
            fetch(second.action, { method: 'POST', body: fd, headers: { Accept: 'text/html' } }).catch(() => {});
          }
          form.hidden = true;
          ok.hidden = false;
        } catch (err) {
          form.dataset.native = '1';
          form.submit();
        }
      });
    });
  }

  /* ---- third-party floating widgets on the new pages ----
     The assistant bubble (side widget in a shadow root) sits over the product, so it is hidden on pages built with
     these sections: by the stylesheet where :has() works, and here for the browsers where it does not.
     The store's live chat button stays; the stylesheet lifts it above the buy bar. */
  function tameWidgets() {
    if (!doc.querySelector('.bs')) return;
    const isAssistant = (el) => el.shadowRoot && el.shadowRoot.querySelector('.fab-glass, [class*="chakra-"]');
    const sweep = () => {
      doc.querySelectorAll('body > div, body > div > div').forEach((el) => {
        if (isAssistant(el)) el.style.display = 'none';
      });
    };
    sweep();
    let runs = 0;
    const timer = setInterval(() => {
      sweep();
      if (++runs > 24) clearInterval(timer);
    }, 700);
    // a widget host added later is held invisible for a moment while it builds, so the bubble never flashes
    if ('MutationObserver' in window) {
      new MutationObserver((muts) => {
        muts.forEach((m) =>
          m.addedNodes.forEach((n) => {
            if (n.nodeType !== 1 || n.tagName !== 'DIV' || n.id || n.className) return;
            n.style.visibility = 'hidden';
            let tries = 0;
            const probe = setInterval(() => {
              if (isAssistant(n)) {
                n.style.display = 'none';
                clearInterval(probe);
              } else if (++tries > 60) {
                n.style.visibility = '';
                clearInterval(probe);
              }
            }, 80);
          })
        );
      }).observe(doc.body, { childList: true });
    }
  }

  /* ---- cart page: redraw the free-shipping line when the cart changes ---- */
  function initCartProgress() {
    const box = doc.querySelector('[data-bs-cart-progress]');
    if (!box || typeof window.subscribe !== 'function') return;
    // 'cart-update' is the theme's own cart event (PUB_SUB_EVENTS.cartUpdate in constants.js)
    window.subscribe('cart-update', () => {
      fetch(`${location.pathname}?section_id=${box.dataset.section}`)
        .then((r) => r.text())
        .then((html) => {
          const next = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-bs-cart-progress]');
          if (next) box.innerHTML = next.innerHTML;
        })
        .catch(() => {});
    });
  }

  // Delivery by province in the buy box: the chosen province's line (days and the cost of one bottle), remembered
  // for the next page where the browser allows it.
  function initDelivery(scope) {
    scope.querySelectorAll('[data-bs-deliv]').forEach((el) => {
      if (el.dataset.bsReady) return;
      el.dataset.bsReady = '1';
      const sel = el.querySelector('select');
      const out = el.querySelector('.bs-deliv__out');
      if (!sel || !out) return;
      const show = () => {
        const opt = sel.options[sel.selectedIndex];
        out.textContent = opt && opt.value ? opt.dataset.text : out.dataset.default;
      };
      let saved = '';
      try { saved = localStorage.getItem('bs-province') || ''; } catch (e) {}
      if (saved && Array.from(sel.options).some((o) => o.value === saved)) sel.value = saved;
      show();
      sel.addEventListener('change', () => {
        show();
        try { localStorage.setItem('bs-province', sel.value); } catch (e) {}
      });
    });
  }

  function init(scope = doc) {
    initReveal(scope);
    initDelivery(scope);
    initRails(scope);
    initBuy(scope);
    initVariants(scope);
    initGallery(scope);
    initFilm(scope);
    initClips(scope);
    initWalls(scope);
    initShipmap(scope);
    initChain(scope);
    initNotify(scope);
    scope.querySelectorAll('[data-bs-story]').forEach(initStory);
  }
  BS.init = init;

  onReady(() => {
    init();
    initBars();
    initCartProgress();
    tameWidgets();
    doc.addEventListener('shopify:section:load', (e) => init(e.target));
  });
})();
